import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Callout,
  EmptyState,
  Icon,
  RowsSkeletonLoader,
  useSidePanel,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/ThemeLottie'
import { PageHeader } from 'components/header/PageHeader'
import { DEFAULT_NUMBER_OF_ROWS } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useGetPaymentLimits } from 'hooks/queries/useGetPaymentLimits'
import { PaymentLimitSheetType, type Limit } from 'types/paymentLimits'
import mockPaymentLimits from '../../../__mocks__/PaymentLimits/paymentLimits.json'
import { PaymentLimitCard } from './components/PaymentLimitCard'
import { PaymentLimitSheet } from './components/PaymentLimitSheet'
import { PaymentLimitSidePanel } from './components/PaymentLimitSidePanel'

export const PaymentLimits = () => {
  const { t } = useTranslation()
  const { currentAccount } = useAccountsContext()

  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false)
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false)
  const [selectedLimit, setSelectedLimit] = useState<Limit | null>(null)

  const {
    data: limitsData,
    isFetching,
    isError,
  } = useGetPaymentLimits({
    accountId: currentAccount?.reference,
    page: 0,
    size: Number(DEFAULT_NUMBER_OF_ROWS),
  })

  const limits = limitsData?.content ?? (mockPaymentLimits?.content as Limit[])

  const hasLimits = limits?.length > 0
  const showEmptyState = !isFetching && !isError && limits?.length === 0

  const {
    isSidePanelOpen,
    sidePanelData: selectedSidePanelLimit,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(limits)

  const handleEditLimit = useCallback((limit: Limit) => {
    setSelectedLimit(limit)
    setIsEditSheetOpen(true)
  }, [])

  const handleCloseEditSheet = useCallback(() => {
    setIsEditSheetOpen(false)
    setSelectedLimit(null)
  }, [])

  const handleViewDetails = useCallback(
    (limit: Limit) => {
      const index = limits?.findIndex(item => item?.ruleId === limit?.ruleId)
      if (index < 0) return
      updateSidePanel(index)
      setIsSidePanelOpen(true)
    },
    [limits, setIsSidePanelOpen, updateSidePanel]
  )

  return (
    <>
      <PageHeader
        description={t('paymentLimits.pageDescription')}
        renderRightSection={
          <Button
            testId="add-limit-button"
            iconElement={<Icon name="Plus" />}
            onClick={() => setIsAddSheetOpen(true)}
          >
            {t('paymentLimits.addLimit')}
          </Button>
        }
      />

      <Box paddingB={24}>
        <Callout>{t('paymentLimits.customerLimitsCallout')}</Callout>
      </Box>

      {isFetching && <RowsSkeletonLoader rows={3} />}

      {hasLimits && (
        <Box flex isFlexWrap gap={16}>
          {limits?.map(limit => (
            <Box key={limit.ruleId} className="w-full md:w-[calc(50%-8px)]">
              <PaymentLimitCard
                limit={limit}
                onEditLimit={handleEditLimit}
                onViewDetails={handleViewDetails}
              />
            </Box>
          ))}
        </Box>
      )}

      {showEmptyState && (
        <EmptyState
          testId="payment-limits-empty-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('paymentLimits.emptyStateTitle')}
          description={t('paymentLimits.emptyStateDescription')}
        />
      )}

      <PaymentLimitSheet
        isOpen={isAddSheetOpen}
        type={PaymentLimitSheetType.ADD}
        onClose={() => setIsAddSheetOpen(false)}
      />

      <PaymentLimitSheet
        isOpen={isEditSheetOpen}
        type={PaymentLimitSheetType.EDIT}
        limit={selectedLimit}
        onClose={handleCloseEditSheet}
      />

      <PaymentLimitSidePanel
        isOpen={isSidePanelOpen}
        limit={selectedSidePanelLimit ?? null}
        onClose={() => setIsSidePanelOpen(false)}
      />
    </>
  )
}
